import { filter, guardArray } from "@msuite/utils/array";
import type { Nullable, TimelineSection } from "@msuite/utils/types";
import { useMemo } from "react";

/** Props Interface */
interface UseVirtualizeTimelineProps<T extends { id: string }, U> {
	timeline: Nullable<Array<TimelineSection<T, U>>>;
	searchValue: string;
	showEmptySections?: boolean;
}

export function useVirtualizeTimeline<T extends { id: string }, U>({
	timeline,
	searchValue,
	showEmptySections = false,
}: UseVirtualizeTimelineProps<T, U>) {
	const filtered = useMemo(() => {
		const filteredTimeline = guardArray(timeline)
			.map((section) => {
				let items = filter([...(section.items ?? [])], searchValue);
				if (items.length === 0 && showEmptySections)
					items = [{ item: null, type: "blank" } as unknown as { type: U; item: T }];
				return { ...section, items };
			})
			.filter((section) => {
				return section.items?.length;
			});
		return filteredTimeline;
	}, [timeline, searchValue]);

	const virtualized = useMemo(() => {
		const virtualized = virtualizeTimeline(filtered);

		return virtualized;
	}, [filtered]);

	return { virtualized };
}

function virtualizeTimeline<T extends { id: string }, U>(timeline: Array<TimelineSection<T, U>>) {
	const array: Array<{
		type: "date" | "item" | "blank";
		date?: string;
		item?: unknown;
	}> = [];

	for (const section of timeline) {
		array.push({
			date: section.date,
			type: "date",
		});

		for (const item of section.items ?? []) {
			if (item.type === "blank") {
				array.push({
					item: null,
					type: "blank",
				});
				continue;
			}
			array.push({
				item: item,
				type: "item",
			});
		}
	}

	return array;
}
